<template>
  <div>
    <CSpinner v-if="!currentCampaign" class="h-96" :size="'small'" />
    <template v-else>
      <CBreadcrumbs :has-filters-in-query="false" :breadcrumb="breadcrumb" />
      <TheHero
        :title="hero.title"
        :description="hero.description"
        :background-url-desktop="heroUrlDesktop"
        :background-url-mobile="heroUrlMobile"
        :icon-url="iconUrl"
        :overlay="hero.overlay"
        template="blue-gradient"
      />
      <ProductListingPageLayout v-if="hasProducts">
        <template #title-desc>
          <ProductListingPageDescription
            v-if="campaignDescription"
            data-test="product-category-description"
            :contentful-description="campaignDescription"
          />
        </template>
        <template #count>
          <span>
            {{ t('number_of_variants') }}:
            <span class="font-bold" data-test="count">{{ data.totalVariantHits }}</span>
          </span>
        </template>

        <template #sort>
          <SortProducts @change-sorting="changeSorting($event)" />
        </template>

        <template #per-page>
          <CDropdown
            :options="productsPerPageOptions"
            :value="String(productsPerPage)"
            label="Antal per sida"
            @update:model-value="updateProductsPerPage"
          />
        </template>

        <template #products>
          <ProductTileWrapper
            v-for="product in currentProducts"
            :key="`${product.id} ${product.name}`"
            :ref="(el) => (productTiles[product.id!] = el)"
            :product="product"
            :badges="badges"
          />
          <div v-if="pending">
            <ProductSkeletonTile v-for="index in productsPerPage" :key="index" />
          </div>
          <div v-else-if="!pending && currentProducts.length !== 0" class="flex w-full mb-6">
            <CPagination :active="activePage" :pages="pages" @update:model-value="updateActivePage" />
          </div>

          <img v-if="lazyLoading" class="mx-auto mt-6 mb-20 w-14 h-14" src="/loaders/lazy-loader.svg" alt="loader" />
        </template>
      </ProductListingPageLayout>
      <pre v-else class="p-12 font-bold text-primary-900">{{ t('campaigns.no_products_text') }}</pre>
    </template>
  </div>
</template>

<script setup lang="ts">
import * as contentfulRichText from '@contentful/rich-text-types';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import {
  useAssortmentStore,
  useCampaignStore,
  useDeliveryStore,
  useProductStore,
  useTrackingStore,
  useUserStore,
  useBrandStore,
} from '@/stores';
import { DELIVERY_EXPRESS, EXPRESS_DELIVERIES } from '@/config/delivery-config';
import type { Product } from '@/types/products';
import type { Campaign } from '@/types/campaign';
import { productsPerPageOptions, sortOptions } from '@/constants';

const { Block, Inline } = contentfulRichText;

type ContentFields = {
  categoryText: typeof Block | typeof Inline;
  hero: {
    fields: any;
  };
} | null;

type Data = {
  totalProductHits: number;
  totalVariantHits: number;
  currentProductCount: number;
  products: Product[];
  contentFields?: ContentFields;
};

const defaultData: Data = {
  totalProductHits: 0,
  totalVariantHits: 0,
  currentProductCount: 0,
  products: [],
  contentFields: null,
};

const route = useRoute();
const { params, query, fullPath } = route;
const router = useRouter();
const { t } = useI18n();
const { public: config } = useRuntimeConfig();
const { $tracking, $searchService, $sentry } = useNuxtApp();

const currentProducts = ref<Product[]>([]);
const lazyLoading = ref(false);
const hasProducts = ref(true);
const timer = ref<ReturnType<typeof setTimeout> | undefined>(undefined);
const isInitialized = ref(false);
const filters = ref(createFilterList(query));
const productTiles = ref<any>({});

const {
  getSelectedDeliveryId,
  getCurrentStockLocation,
  getCurrentStockLocations,
  selectedLocations,
  rawLocations: storeRawLocations,
} = storeToRefs(useDeliveryStore());
const { brandConfig } = useBrandStore();
const { isLoggedIn } = storeToRefs(useUserStore());
const { campaigns, assortments } = storeToRefs(useCampaignStore());
const { trackingId } = storeToRefs(useTrackingStore());

const productStore = useProductStore();

const { resetProductHistory } = productStore;
const { productHistory, languageConfig } = storeToRefs(productStore);
const { assortmentId } = storeToRefs(useAssortmentStore());
const { setFacetsFromSearchResult, clearFilters } = useProductStore();
const { registerSessionStartedHook } = useUserStore();
const { fetchEntry } = useContentful();

type Props = {
  isAssortmentPage: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  isAssortmentPage: false,
});

const activePage = computed(() => {
  const page = Number(route.query.page);
  return page > 0 ? page : 1;
});

const startIndex = computed(() => (activePage.value - 1) * productsPerPage.value);

const productsPerPage = computed(() => {
  const perPage = Number(route.query.perPage);
  return perPage > 0 ? perPage : 20;
});

const sortOrder = computed(() => {
  const sortQuery = route.query.sort as string;
  return sortOptions.includes(sortQuery) ? sortQuery : undefined;
});

const pages = computed(() => {
  const totalProducts = data.value.totalProductHits;

  return Math.ceil(totalProducts / productsPerPage.value);
});

const isExpressDelivery = computed(() => isLoggedIn.value && EXPRESS_DELIVERIES.includes(getSelectedDeliveryId.value));

const currentCampaign = computed((): Campaign | undefined => {
  if (!params) {
    return undefined;
  }

  const data = props.isAssortmentPage ? assortments : campaigns;
  return data.value.find((data) => data.slug === params.slug);
});

const slug = computed(() => {
  if (!currentCampaign.value) {
    return '';
  }
  if (!params) {
    return '';
  }
  return params.slug;
});
const breadcrumb = computed(() => {
  return {
    name: currentCampaign.value?.name,
    parents: [
      {
        slug: props.isAssortmentPage ? '/sortiment' : '/kampanjer',
        name: t(`campaigns.${campaignTypeText.value}_breadcrumb_title`),
      },
    ],
  };
});
const campaignDescription = computed(() => data.value.contentFields?.categoryText);
const campaignTypeText = computed(() => (props.isAssortmentPage ? 'assortment' : 'campaign'));

const heroUrlDesktop = computed(() => {
  return currentCampaign.value?.content?.hero?.heroDesktopUrl;
});
const heroUrlMobile = computed(() => {
  return currentCampaign.value?.content?.hero?.heroMobileUrl;
});
const iconUrl = computed(() => {
  return currentCampaign.value?.content?.hero?.iconUrl;
});
const hero = computed(() => {
  if (currentCampaign.value?.content?.hero) {
    return {
      ...currentCampaign.value.content.hero,
    };
  } else {
    return { title: currentCampaign.value?.name, description: '', overlay: false };
  }
});
const badges = computed(() => {
  if (!currentCampaign.value) return [];

  return getBadges([currentCampaign.value], [currentCampaign.value.id]);
});

const rawLocations = computed(() => {
  const locations = storeRawLocations.value[getSelectedDeliveryId.value];
  return locations.length ? locations : getCurrentStockLocations.value;
});

const updateActivePage = (newPage: number) => {
  if (newPage === activePage.value) return;

  const query = { ...router.currentRoute.value.query };
  router.push({ path: route.path, query: { ...query, page: newPage } });

  const firstProductId = currentProducts.value[0]?.id;

  resetProductHistory();

  if (!firstProductId) return;

  scrollToProduct(firstProductId, 'smooth');
};

const updateProductsPerPage = (value: string) => {
  router.push({ path: route.path, query: { ...route.query, perPage: value, page: undefined } });
};

const productImpression = (products: Product[], side?: string) => {
  if (process.server) return;
  $tracking.emit('productImpression', {
    products,
    list: config.gtm.PRODUCT_LIST,
    side,
  });
};

const getProducts = async (): Promise<Data> => {
  if (!currentCampaign.value) {
    return defaultData;
  }
  const products: Product[] = [];
  const currentFilters = [...filters.value];
  const sortBy = [];
  const additionalFilters = [];

  try {
    if (isExpressDelivery.value) {
      additionalFilters.push({
        attributeName: 'stockedAt',
        name: 'stockedAt',
        selected: [getCurrentStockLocation.value],
        type: 'distinct',
      });
      if (getSelectedDeliveryId.value === DELIVERY_EXPRESS) {
        additionalFilters.push({
          attributeName: 'filterTags',
          name: 'filterTags',
          selected: ['express-delivery'],
          type: 'distinct',
        });
      }
    } else if (brandConfig.additions.useStockedAtForStockedFilter) {
      if (!process.server) {
        const index = currentFilters.findIndex((filter) => filter.name === STOCKED_AT_NAME);
        if (index !== -1) {
          currentFilters.splice(index, 1);
          additionalFilters.push({
            attributeName: 'stockedAt',
            name: STOCKED_AT_NAME,
            selected: rawLocations.value,
            type: 'distinct',
          });
        }
      }
    }
    if (sortOrder) {
      const sortingParameter = {
        type: 'attribute',
        attributeName: 'name',
        order: sortOrder.value,
      };

      sortBy.push(sortingParameter);
    }

    const body = {
      attribute: {
        name: 'campaignIds',
        value: currentCampaign.value.id,
      },
      resultsOptions: {
        skip: startIndex.value,
        take: productsPerPage.value,
        sortBy,
        facets: [...currentFilters, ...additionalFilters],
      },
    };

    const fetchedResults = await getProductsByAttribute(
      $searchService,
      brandConfig,
      { body },
      {
        trackingId: trackingId.value,
        assortmentId: assortmentId.value ?? 'default',
        languageConfig: languageConfig.value,
      },
    );

    const { facets, facetsData, side } = fetchedResults;
    setFacetsFromSearchResult({ facets, facetsData });
    let totalVariantHits = 0;
    let currentProductCount = 0;
    let totalProductHits = 0;

    if (!fetchedResults?.products?.length) {
      totalVariantHits = currentProductCount = 0;
    } else {
      const { products: fetchedProducts, count, VariantResults_TotalItems: variantResultTotalItems } = fetchedResults;

      totalProductHits = count;
      totalVariantHits = variantResultTotalItems;

      fetchedProducts.forEach((product: Product) => {
        products.push(product);
      });
      currentProductCount = products.length;

      productImpression(
        fetchedProducts.map((product: Product, index: number) => {
          return { ...product, listPosition: currentProductCount - fetchedProducts.length + index + 1 };
        }),
        side,
      );
    }
    return { totalProductHits, totalVariantHits, products, currentProductCount };
  } catch (error: any) {
    $sentry.captureException(error);
    if (error?.response?.status === 400 || error?.request?.res?.statusCode === 400) {
      hasProducts.value = false;
    } else if (error?.response?.status === 404) {
      createError({ statusCode: 404, message: 'Failed to get campaign products' });
    } else {
      createError({ statusCode: 500, message: 'Failed to get campaign products' });
    }
    return defaultData;
  } finally {
    lazyLoading.value = false;
  }
};

const { data, pending, refresh } = await useAsyncData(
  `campaign-${slug.value}`,
  async (): Promise<Data> => {
    const { totalVariantHits, totalProductHits, currentProductCount, products } = await getProducts();

    const entry = await fetchEntry(
      {
        contentType: 'productListingPage',
        'fields.slug': `${slug.value}`,
      },
      { reportErrors: false },
    );

    const contentFields = entry.data?.fields;

    return { products, contentFields, totalVariantHits, totalProductHits, currentProductCount };
  },

  {
    lazy: true,
    default: () => defaultData,
  },
);

const initRefresh = () => {
  currentProducts.value = [];
  refresh();
};

const changeSorting = (newSortOrder: string) => {
  router.push({ path: route.path, query: { ...route.query, sort: newSortOrder } });
};

const scrollToProduct = (productId: string, behavior = 'smooth' as ScrollBehavior) => {
  const position = productTiles.value[productId]?.$el.offsetTop;
  const SCROLL_POS_PADDING = 50;
  if (position) window.scrollTo({ top: position - SCROLL_POS_PADDING, behavior });
};

watch(currentCampaign, (to, from) => {
  if (to === from) return;
  if (slug.value) {
    resetProductHistory();
    initRefresh();
  } else {
    $sentry.captureException(new Error('Missing value for property slug'));
  }
});

watch(
  () => data.value.products,
  (products) => {
    currentProducts.value = products;
  },
  { immediate: true },
);

watch(
  () => slug,
  () => {
    if (slug.value) {
      initRefresh();
    } else {
      $sentry.captureException(new Error('Missing value for propery slug'));
    }
  },
);

watch(
  () => route.query,
  (newQuery, oldQuery) => {
    const { page: _newPage, sort: _newSort, ...newQueryCleaned } = newQuery;
    const { page: _oldPage, sort: _oldSort, ...oldQueryClearned } = oldQuery;

    const filtersDiffer = JSON.stringify(newQueryCleaned) !== JSON.stringify(oldQueryClearned);

    if (
      (oldQuery.page !== newQuery.page || oldQuery.perPage !== newQuery.perPage || oldQuery.sort !== newQuery.sort) &&
      !filtersDiffer
    ) {
      resetProductHistory();
      initRefresh();
      return;
    }

    if (oldQuery.page && newQuery.page) {
      if (filtersDiffer) {
        router.push({ query: newQueryCleaned });
      }
    } else {
      router.push({ query: newQueryCleaned });
    }

    filters.value = createFilterList(route.query);
    data.value.currentProductCount = 0;
    resetProductHistory();
    if (slug.value) {
      initRefresh();
    } else {
      $sentry.captureException(new Error('Missing value for property slug'));
    }
  },
);

watch(pending, (pending) => {
  const prevVisitedProduct = productHistory.value.productId;
  if (!pending && prevVisitedProduct) scrollToProduct(prevVisitedProduct);
});

watch(getSelectedDeliveryId, (toId, fromId) => {
  if (!isInitialized.value) {
    return;
  }
  if (!brandConfig.centralStorage) {
    initRefresh();
  } else {
    if (isExpressDelivery.value && !getCurrentStockLocation.value) return;
    if (EXPRESS_DELIVERIES.includes(toId) || EXPRESS_DELIVERIES.includes(fromId)) {
      initRefresh();
    }
  }
});

watch(
  selectedLocations,
  (to, from) => {
    if (!isInitialized.value) {
      return;
    }
    if (!brandConfig.centralStorage) {
      initRefresh();
    } else {
      const currentExpressOptionChangedStore =
        isExpressDelivery.value && to[getSelectedDeliveryId.value] !== from[getSelectedDeliveryId.value];

      if (currentExpressOptionChangedStore) {
        initRefresh();
      }
    }
  },
  { deep: true },
);

onMounted(() => {
  // Throw out user if not ready in 15 seconds
  timer.value = setTimeout(() => {
    if (!currentCampaign.value) router.push(props.isAssortmentPage ? '/sortiment' : '/kampanjer');
  }, 15000);

  if (data.value.products.length) {
    productImpression(data.value.products);
  }
  const initFunc = () => {
    isInitialized.value = true;
    initRefresh();
  };

  if (isLoggedIn.value) {
    initFunc();
  } else {
    registerSessionStartedHook(initFunc);
  }
});

onBeforeUnmount(() => {
  clearTimeout(timer.value);
  clearFilters();
});

const getCanonicalCampaignPath = (fullPath: string): string => {
  return fullPath.split('?')[0];
};

useHead(() => ({
  title: currentCampaign.value?.name || t('campaign'),
  link: [{ rel: 'canonical', href: `${config.baseUrl}${getCanonicalCampaignPath(fullPath)}` }],
}));

provide('attributesToHide', []);
</script>

<script lang="ts">
export default defineComponent({
  name: 'CampaignPage',
});
</script>
